import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ServicesCarousel from '../components/ServicesCarousel';
import FleetCarousel from '../components/FleetCarousel';
import ContactForm from '../components/ContactForm';
import SocialLinks from '../components/SocialLinks';
import LogoTicker from '../components/LogoTicker';
import Testimonials from '../components/Testimonials';
import HowWeWork from '../components/HowWeWork';
import Hero from '../components/Hero';
import '../styles/components/Hero.css';
import '../styles/components/AboutVideo.css';

const HomePage: React.FC = () => {
  const { t } = useTranslation();

  // Function to scroll to about section
  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Add useEffect to help manage how scrolling is handled
  useEffect(() => {
    // Force an initial scroll event to set the header state correctly
    window.dispatchEvent(new Event('scroll'));

    // This helps ensure the header is in the correct state when the page loads
    return () => {
      // Clean up any scroll-related effects if needed
    };
  }, []);

  return (
    <div className="home-page" style={{ paddingTop: '160px' }}>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-image-container">
          <Hero />
        </div>
        <div className="hero-content container" style={{ zIndex: 3 }}>
          <h1 className="hero-title">
            {t('hero_title_first_line')}<br />
            {t('hero_title_second_line')}
          </h1>
          <p className="hero-description">{t('hero_subtitle')}</p>
          <a
            href="https://book.mylimobiz.com/v4/(S(dbiawds2qobzoxiuj02esmsm))/travellimo"
            className="cta-button primary-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="desktop-button-text">{t('hero_cta')}</span>
            <span className="mobile-button-text">
              {t('hero_cta_first_line')}<br />
              {t('hero_cta_second_line')}<br />
              {t('hero_cta_third_line')}
            </span>
          </a>
        </div>
      </section>

      {/* About Us Section */}
      <section className="about-section" id="about">
        <div className="container">
          <h2 className="section-title">{t('about_title')}</h2>
          <div className="about-content">
            <div className="about-grid">
              <div className="about-col">
                <p className="about-text">
                  <span className="about-highlight">Airline Crew Transportation</span> {t('about_description')}
                </p>
                <div className="about-stat-container">
                  <div className="about-stat">
                    <span className="about-stat-number">30+</span>
                    <span className="about-stat-label">{t('about_years')}</span>
                  </div>
                  <div className="about-stat">
                    <span className="about-stat-number">7</span>
                    <span className="about-stat-label">{t('about_global_locations')}</span>
                  </div>
                  <div className="about-stat">
                    <span className="about-stat-number">24/7</span>
                    <span className="about-stat-label">{t('about_service')}</span>
                  </div>
                </div>
              </div>
              <div className="about-col">
                <p className="about-text">{t('about_second_paragraph')}</p>
                <a href="/#contact" className="about-cta">
                  <span>{t('get_in_touch')}</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </div>
            </div>

            {/* About Video */}
            <div className="about-video-container">
              <div style={{ padding: "56.25% 0 0 0", position: "relative" }}>
                <iframe
                  src="https://player.vimeo.com/video/1088523405?h=1cdac15443&badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&loop=1&title=0&byline=0&portrait=0&controls=0&muted=1&background=1"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                  title="Air Crew Trans"
                ></iframe>
              </div>
              <script src="https://player.vimeo.com/api/player.js"></script>
            </div>
          </div>
        </div>
      </section>

      {/* In Good Company Section */}
      <section className="in-good-company-section" style={{ marginBottom: '2rem' }}>
        <div className="container">
          <h2 className="section-title">{t('in_good_company_title')}</h2>
          <div className="section-description" style={{ textAlign: 'left', margin: '2rem 0 3rem 0', marginLeft: 0 }}>
            <p style={{ paddingTop: '5px' }}>{t('in_good_company_description')}</p>
          </div>
          <LogoTicker className="logo-ticker-container" />
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <div className="container">
          <h2 className="section-title">{t('services_title')}</h2>
          <div className="section-description">
            <p>{t('services_description')}</p>
          </div>
          <ServicesCarousel className="services-carousel-container" />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section" id="testimonials">
        <div className="container">
          <h2 className="section-title">{t('testimonials_title')}</h2>
          <div className="section-description">
            <p>{t('testimonials_description')}</p>
          </div>
          <Testimonials className="testimonials-slider-container" />
        </div>
      </section>

      {/* Fleet Section */}
      <section className="homepage-fleet-section" id="fleet" style={{ backgroundColor: 'var(--pure-white)', padding: '4rem 0' }}>
        <FleetCarousel className="fleet-carousel-container" />
      </section>

      {/* How We Work Section */}
      <section className="how-we-work-section" id="how-we-work" style={{ backgroundColor: 'var(--pure-white)', padding: '4rem 0' }}>
        <div className="container">
          <h2 className="section-title" style={{ color: 'var(--rich-black)' }}>{t('how_we_work_title')}</h2>
          <div className="section-description">
            <p style={{ color: 'var(--rich-black)' }}>{t('how_we_work_description')}</p>
          </div>
          <HowWeWork className="how-we-work-component" />
        </div>
      </section>

      {/* Contact Us Section */}
      <ContactForm />
    </div>
  );
};

export default HomePage; 